import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Box, Typography, Button, IconButton, Tooltip, Badge, Stack, TextField, InputAdornment } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';

import API, { RBAC } from '../../services/api';
import ButtonNotes from '../../components/ButtonNotes';
import CustomSnackBar from '../../components/CustomSnackBar';
import MainText from '../../components/MainText';
import { PendingActions, Image, UploadFile, CheckCircleRounded, Checklist, Delete, Search } from '@mui/icons-material';
import { addTab } from '../../tabs/tabsSlice';
import { useAuth } from '../../auth/AuthProvider';
import { hasPerm, hasAny } from '../../auth/permissions';
import dayjs from 'dayjs';
import Visibility from '@mui/icons-material/Visibility';
import CheckCircle from '@mui/icons-material/CheckCircle';
import CustomModal from '../../components/CustomModal';
import InvoiceProductReview from '../../components/InvoiceProductReview';
import { NoteAlt } from '@mui/icons-material';
import { motion } from 'framer-motion';
const formatYMD = (v) => {
  if (v === null || v === undefined) return "Sin fecha";
  const s = String(v).trim();
  if (!s) return "Sin fecha";
  // 1) Coincidencia exacta YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  }
  // 2) ISO o cualquier cosa que empiece con YYYY-MM-DD
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const [, Y, M, D] = m;
    return `${D}/${M}/${Y}`;
  }
  // 3) Último intento: Date nativa
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  }
  // 4) No se pudo formatear
  return "Sin fecha";
};
const formatYMDHMS = (v) => {
  if (v === null || v === undefined) return "Sin fecha";
  const s = String(v).trim();
  if (!s) return "Sin fecha";

  // 1) Coincidencia exacta YYYY-MM-DD HH:mm:ss (o similar)
  // Esta regex es más flexible para incluir T, Z o espacios
  const fullMatch = s.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2}):(\d{2}(?:\.\d+)?)(Z)?)?/
  );

  if (fullMatch) {
    // Usaremos la Date nativa para manejar la conversión de zona horaria (como 'Z' o ISO)
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, "0");
      const mi = String(d.getMinutes()).padStart(2, "0");
      const ss = String(d.getSeconds()).padStart(2, "0");

      return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
    }
  }

  // 2) Último intento: Date nativa para cualquier otro formato válido
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");

    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  // 3) No se pudo formatear
  return "Sin fecha";
};

const normalizeSearchText = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const resolveProductId = (row) => row?.product?.id ?? row?.product_id ?? row?.product ?? null;

const resolveFormatSignature = (row) => {
  const fmtId = row?.prformat_id ?? row?.prformat?.id ?? null;
  if (fmtId !== null && fmtId !== undefined && fmtId !== '') return `id:${fmtId}`;
  const fmtName =
    row?.prformat?.name ??
    row?.prformat_name ??
    row?.prformat ??
    row?.format ??
    row?.format_name ??
    '';
  const normalized = normalizeSearchText(fmtName);
  return normalized ? `name:${normalized}` : 'none';
};

const resolveLineKey = (row, type = 'detail') => {
  const productId = resolveProductId(row) ?? 'x';
  const dueDate = row?.dueDate || row?.expiration || row?.batch || row?.lot || '';
  return `${type}:${productId}:${resolveFormatSignature(row)}:${String(dueDate || '')}`;
};

function ConfirmInvoice() {
  const { permissions, isSuperuser, company } = useAuth();
  const canUseLogical = isSuperuser || hasAny(permissions, ['view_catalogs', 'manage_catalogs']);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [commonData, setCommonData] = useState({ signedById: null });
  const [notes, setNotes] = useState([]);
  const [logicalWarehouses, setLogicalWarehouses] = useState([]);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
  const [loading, setLoading] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState([]);
  const [openProducts, setOpenProducts] = useState(false);
  const [productsReviewed, setProductsReviewed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const uploadInputRef = useRef(null);
  const [uploadTargetId, setUploadTargetId] = useState(null);
  const formatCurrency = useCallback((value) => {
    return new Intl.NumberFormat('es-GT', { style: 'currency', currency: 'GTQ' }).format(value || 0);
  }, []);
  const handleUploadClick = (row) => {
    setUploadTargetId(row.id);
    uploadInputRef.current?.click();
  };
  const handleUploadChange = async (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f || !uploadTargetId) return;
    try {
      const fd = new FormData();
      fd.append('invoiceDocuments', f);
      await API.patch(`invoice/${uploadTargetId}/`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const invRes = await API.get('invoice/unconfirmed/');
      setInvoices(invRes.data);
    } catch (err) {
      console.error('Error adjuntando documento', err);
    } finally {
      e.target.value = '';
      setUploadTargetId(null);
    }
  };
  const handleDeleteDocument = async (invoiceId) => {
    if (!invoiceId) return;
    const confirmDelete = window.confirm('¿Eliminar este documento de la factura?');
    if (!confirmDelete) return;
    try {
      await API.patch(`invoice/${invoiceId}/`, { invoiceDocuments: null });
      const invRes = await API.get('invoice/unconfirmed/');
      setInvoices(invRes.data);
    } catch (err) {
      console.error('Error eliminando documento', err);
    }
  };

  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(addTab({ label: 'Confirmar factura', path: '/invoice/confirm' }));
  }, [dispatch]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const invRes = await API.get('invoice/unconfirmed/');
        if (mounted) {
          const data = Array.isArray(invRes.data) ? invRes.data : [];
          setInvoices(data);
        }
      } catch (error) {
        if (mounted) setInvoices([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    if (!canUseLogical) {
      setLogicalWarehouses([]);
      return () => { mounted = false; };
    }
    (async () => {
      try {
        const params = company?.id ? { company: company.id } : undefined;
        const res = await RBAC.logicalWarehouses.list(params);
        if (mounted) setLogicalWarehouses(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (mounted) setLogicalWarehouses([]);
      }
    })();
    return () => { mounted = false; };
  }, [canUseLogical, company?.id]);

  const resolveLegacyWarehouseText = useCallback((note) => {
    const direct = note?.legacy_warehouse_text || note?.warehouse;
    if (direct !== undefined && direct !== null) {
      const cleaned = String(direct).trim();
      if (cleaned) return cleaned;
    }
    const logicalId = note?.logical_warehouse_id ?? note?.logical_warehouse?.id ?? null;
    if (logicalId == null) return null;
    const match = (logicalWarehouses || []).find((l) => String(l.id) === String(logicalId));
    const fallback =
      match?.code ||
      match?.name ||
      note?.logical_warehouse?.code ||
      note?.logical_warehouse?.name ||
      null;
    if (!fallback) {
      const fallbackId = String(logicalId || '').trim();
      return fallbackId || null;
    }
    const cleaned = String(fallback).trim();
    return cleaned || null;
  }, [logicalWarehouses]);

  const unifyDetails = (invoicesData) => {
    const map = new Map();
    const extras = [];
    invoicesData.forEach(inv => {
      (inv.invoice_detail || []).forEach(d => {
        const lineKey = resolveLineKey(d, 'detail');
        if (!map.has(lineKey)) {
          map.set(lineKey, {
            ...d,
            quantity: d.quantity,
            quantityRe: '',
            sources: [{ invoiceId: inv.id, detailId: d.id, quantity: d.quantity }],
          });
        } else {
          const existing = map.get(lineKey);
          existing.quantity += d.quantity;
          existing.sources.push({ invoiceId: inv.id, detailId: d.id, quantity: d.quantity });
          map.set(lineKey, existing);
        }
      });
      (inv.invoice_bonus || []).forEach(b => {
        const lineKey = resolveLineKey(b, 'bonus');
        const existingIndex = extras.findIndex((item) => item.lineKey === lineKey);
        if (existingIndex >= 0) {
          extras[existingIndex].quantity += b.quantity;
          extras[existingIndex].sources.push({ invoiceId: inv.id, detailId: b.id, quantity: b.quantity });
          return;
        }
        extras.push({
            id: b.id,
            lineKey,
            product: b.product || { id: b.product_id },
            product_id: b.product_id,
            code: b.code,
            description: b.description || 'Producto promocional',
            quantity: b.quantity,
            prformat: b.prformat,
            quantityRe: b.quantityRe ?? 0,
            is_bonus: true,
            sources: [{ invoiceId: inv.id, detailId: b.id, quantity: b.quantity }],
          });
      });
    });
    return [...Array.from(map.values()), ...extras];
  };

  const handleSelectionChange = async (ids) => {
    setSelectedIds(ids);
    if (ids.length === 0) {
      setSelectedInvoices([]);
      setInvoiceDetails([]);
      setNotes([]);
      setProductsReviewed(false);
      return;
    }
    try {
      const responses = await Promise.all(ids.map(id => API.get(`invoice/${id}/`)));

      const data = responses.map(r => r.data);
      setSelectedInvoices(data);
      setCommonData(prev => ({ ...prev, signedById: data[0]?.signedById || null }));
      const unified = unifyDetails(data);
      console.log('Detalles unificados:', unified);
      setInvoiceDetails(unified);
      setNotes(prev => prev.map(n => ({ ...n, linkedInvoices: (n.linkedInvoices || []).filter(id => ids.includes(id)) })));
      setProductsReviewed(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleConfirm = async () => {
    if (selectedInvoices.length === 0 || notes.length === 0 || !productsReviewed) return;
    try {
      setLoading(true);

      const distByInvoice = {};
      invoiceDetails.forEach(item => {
        const total = parseInt(item.quantityRe) || 0;
        let remaining = total;
        item.sources.forEach(src => {
          const assign = Math.min(remaining, src.quantity);
          if (!distByInvoice[src.invoiceId]) distByInvoice[src.invoiceId] = [];
          distByInvoice[src.invoiceId].push({ id: src.detailId, quantityRe: assign });
          remaining -= assign;
        });
      });

        const createdNotes = await Promise.all(
          notes.map(async n => {
            if (n.id) return n;
            const payload = {
              number: n.number,
              comment: n.comment,
            };
            if (n.date) {
              payload.date = dayjs(n.date).format('YYYY-MM-DD');
            }
            const logicalId = n.logical_warehouse_id ?? n.logical_warehouse?.id ?? null;
            if (logicalId) payload.logical_warehouse_id = logicalId;
            const legacyText = resolveLegacyWarehouseText(n);
            if (legacyText) {
              payload.legacy_warehouse_text = legacyText;
              if (!logicalId) payload.warehouse = legacyText;
            }
            const res = await API.post('receive-note/', payload);
            return { ...n, id: res.data.id };
          })
        );

      for (const inv of selectedInvoices) {
        for (const n of createdNotes) {
          if (n.linkedInvoices && n.linkedInvoices.includes(inv.id)) {
            const receivingPayload = {
              invoice: inv.id,
              reNote: n.id,
              comment: n.comment || ''
            };
            if (company?.id) {
              receivingPayload.company_id = company.id;
            }
            await API.post('receiving-note/', receivingPayload);
          }
        }

        await API.post(`invoice/${inv.id}/confirm/`, {
          signedById: commonData.signedById,
          invoiceStatus: 'ICF',
          invoice_detail: distByInvoice[inv.id] || []
        });
      }

      setSnack({ open: true, message: 'Factura confirmada', severity: 'success' });
      setSelectedInvoices([]);
      setSelectedIds([]);
      setNotes([]);
      setInvoiceDetails([]);
      setProductsReviewed(false);
      const invRes = await API.get('invoice/unconfirmed/');
      setInvoices(invRes.data);
    } catch (error) {
      console.error(error);
      setSnack({ open: true, message: 'Error al confirmar', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewProducts = async (event, row) => {
    event.stopPropagation();
    if (selectedIds.length === 0) {
      await handleSelectionChange([row.id]);
    }
    setOpenProducts(true);
  };


  const missingReasons = React.useMemo(() => {
    const reasons = [];
    if (!hasPerm(permissions, 'manage_invoice')) {
      reasons.push('Sin permiso para confirmar');
      return reasons;
    }
    if (!commonData?.signedById) {
      reasons.push('Selecciona un firmante');
    }
    if (!selectedIds || selectedIds.length === 0) {
      reasons.push('Selecciona al menos 1 factura');
    }
    if (!invoiceDetails || invoiceDetails.length === 0) {
      reasons.push('No hay detalles de factura');
    }
    const selectedIdSet = new Set((selectedIds || []).map((id) => String(id)));
    const missingNotes = (notes || []).filter((note) => {
      const links = (note?.linkedInvoices || []).map((id) => String(id));
      return links.length === 0 || !links.some((id) => selectedIdSet.has(id));
    });
    if (!notes || notes.length === 0) {
      reasons.push('Faltan notas por asociar / revisar');
    } else if (missingNotes.length > 0) {
      reasons.push('Faltan notas por asociar / revisar');
    }
    const missingQtyCount = (invoiceDetails || []).filter((d) => {
      const required = Number(d?.quantity ?? 0);
      const qty = Number(d?.quantityRe);
      if (!Number.isFinite(qty) || qty <= 0) return true;
      if (Number.isFinite(required) && required > 0 && qty > required) return true;
      return false;
    }).length;
    if (missingQtyCount > 0) {
      reasons.push(`Faltan cantidades por validar en ${missingQtyCount} ítems`);
    }
    if (!productsReviewed) {
      reasons.push('Revisa y guarda las cantidades');
    }
    return reasons;
  }, [commonData?.signedById, invoiceDetails, notes, permissions, productsReviewed, selectedIds]);

  const confirmDisabled = loading || missingReasons.length > 0;

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), 250);
    return () => clearTimeout(handle);
  }, [searchTerm]);

  const filteredInvoices = React.useMemo(() => {
    const tokens = normalizeSearchText(debouncedSearch).split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return invoices;
    return (invoices || []).filter((row) => {
      const total = Number(row?.total || 0);
      const searchable = normalizeSearchText([
        row?.number,
        row?.series,
        row?.supplier?.companyName,
        row?.supplier?.legalName,
        row?.supplier,
        row?.total,
        Number.isFinite(total) ? total.toFixed(2) : '',
        row?.date,
        row?.createdAt,
        row?.invoiceStatus,
        row?.paymentType,
      ].join(' '));
      return tokens.every((token) => searchable.includes(token));
    });
  }, [debouncedSearch, invoices]);

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'number', headerName: 'Numero de factura', width: 200 },
    { field: "date", headerName: "Fecha factura", width: 130, renderCell: ({ row }) => formatYMD(row?.date) },
    { field: "createdAt", headerName: "Fecha creado", width: 130, renderCell: ({ row }) => formatYMDHMS(row?.createdAt) },
    {
      field: 'total',
      headerName: 'Total',
      width: 120,
      renderCell: ({ row }) => {
        const baseTotal = Number(row?.total || 0) || 0;
        const adjustment = Number(row?.invoice_adjustment || 0) || 0;
        return formatCurrency(baseTotal + adjustment);
      }
    },
    { field: 'paymentType', headerName: 'Tipo de pago', width: 120 },
    {
      field: 'supplier',
      headerName: 'Proveedor',
      width: 150,
      renderCell: (params) => (
        <span>{params.row.supplier?.companyName || 'Sin proveedor'}</span>
      )
    },
    {
      field: 'attach',
      headerName: 'Adjuntar',
      width: 110,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const isAttached = Boolean(params.row.invoiceDocuments);

        return (
          <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
            <Tooltip
              title={isAttached ? 'Cambiar documento' : 'Adjuntar imagen o PDF'}
              arrow
            >
              <div style={{ display: 'inline-block' }}>
                <motion.div
                  whileHover={{ y: -1, scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  style={{ display: 'inline-block' }}
                >
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    badgeContent={
                      isAttached ? (
                        <CheckCircleRounded
                          fontSize="small"
                          sx={{
                            color: 'success.main',
                            bgcolor: 'common.white',
                            borderRadius: '50%',
                            boxShadow: 1,
                          }}
                        />
                      ) : null
                    }
                  >
                    <IconButton
                      size="small"
                      aria-label={isAttached ? 'Cambiar documento' : 'Adjuntar documento'}
                      onClick={(e) => {
                        e.stopPropagation();           // evita seleccionar la fila
                        handleUploadClick(params.row); // tu logica
                      }}
                      sx={(theme) => ({
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        p: 0,
                        background: isAttached
                          ? 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)'
                          : 'linear-gradient(135deg, #10173c 0%, #10173c 100%)',
                        color: 'common.white',
                        boxShadow: isAttached
                          ? '0 6px 14px rgba(46,125,50,.25)'
                          : '0 6px 14px rgba(21,101,192,.25)',
                        border: '1px solid',
                        borderColor: 'rgba(255,255,255,.18)',
                        backgroundSize: '200% 200%',
                        transition:
                          'background-position .35s ease, box-shadow .25s ease, transform .2s ease',
                        '&:hover': {
                          backgroundPosition: 'right center',
                          boxShadow: isAttached
                            ? '0 10px 20px rgba(46,125,50,.30)'
                            : '0 10px 20px rgba(21,101,192,.30)',
                        },
                        '&:active': { transform: 'translateY(0.5px)' },
                        '&:focus-visible': {
                          outline: 'none',
                          boxShadow: isAttached
                            ? `0 0 0 3px ${theme.palette.success.light}`
                            : `0 0 0 3px ${theme.palette.primary.light}`,
                        },
                      })}
                    >
                      {/* Icono compacto para DataGrid */}
                      <UploadFile fontSize="small" />
                    </IconButton>
                  </Badge>
                </motion.div>
              </div>
            </Tooltip>
            {isAttached && (
              <Tooltip title="Eliminar documento" arrow>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteDocument(params.row.id);
                  }}
                  sx={{ width: 32, height: 32 }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      width: 90,
      headerName: 'Productos',
      sortable: false,
      renderCell: (params) => (
        <IconButton color="primary" onClick={(e) => handleViewProducts(e, params.row)}>
          <Checklist />
        </IconButton>
      ),
    },
  ];

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      <MainText
        title="Confirmar factura"
        subtitle="Confirmacion de facturas sin notas de entrada"
        icon={PendingActions}
      />

      {selectedIds.length > 0 && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              pb: 0.75,
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
            }}
          >
            <NoteAlt sx={{ fontSize: 20, color: 'text.secondary' }} />
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, letterSpacing: 0.3, color: 'text.primary' }}
            >
              Notas de entrada
            </Typography>
          </Box>

            <ButtonNotes
              notes={notes}
              setNotes={setNotes}
              invoice={commonData}
              setInvoice={setCommonData}
              validateUnique
              selectedInvoices={selectedInvoices}
              logicalWarehouses={logicalWarehouses}
              canUseLogical={canUseLogical}
            />

          {hasPerm(permissions, 'manage_invoice') && (
            <Tooltip
              title={missingReasons.length ? missingReasons.join(' · ') : 'Listo para confirmar'}
              arrow
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckCircle />}
                  sx={{ mt: 2, px: 3, py: 1, fontWeight: 'bold', alignSelf: 'flex-start' }}
                  onClick={handleConfirm}
                  disabled={confirmDisabled}
                >
                  Confirmar
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      )}

      <TextField
        size="small"
        value={searchTerm}
        onChange={(event) => setSearchTerm(event.target.value)}
        placeholder="Buscar factura, proveedor, serie o monto"
        sx={{ maxWidth: 420 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
      />

      <DataGrid
        rows={filteredInvoices}
        columns={columns}
        autoHeight
        checkboxSelection
        onRowSelectionModelChange={handleSelectionChange}
        rowSelectionModel={selectedIds}
      />
      <input
        ref={uploadInputRef}
        type="file"
        accept="image/*,application/pdf"
        onChange={handleUploadChange}
        style={{ display: 'none' }}
      />

      <CustomModal
        open={openProducts}
        handleClose={() => setOpenProducts(false)}
        setOpen={setOpenProducts}
        title="Productos"
        icon={Visibility}
        width="80%"
        component={InvoiceProductReview}
        componentProps={{
          details: invoiceDetails,
          setDetails: setInvoiceDetails,
          onSave: () => setProductsReviewed(true),
        }}
      />

      <CustomSnackBar
        open={snack.open}
        setOpen={(val) => setSnack(prev => ({ ...prev, open: val }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

export default ConfirmInvoice;










